import React, { useState } from 'react';
import { ProposalState, ProposalStatus } from '../../models/clinicAgent';
import { proposalTitle } from '../../services/clinicAgentProposals';
import { ProposalPayloadView } from './ProposalPayload';
import ProposalCard from './ProposalCard';

interface ProposalDayGroupCardProps {
  // Proposals that are the same item on different days, sorted Monday→Sunday.
  proposals: ProposalState[];
  onResolve: (proposalId: string, decision: 'accept' | 'reject') => void;
  onResolveMany: (proposalIds: string[], decision: 'accept' | 'reject') => void;
}

// English day name → short Spanish label for the chips.
const DAY_LABELS: Record<string, string> = {
  Monday: 'Lun',
  Tuesday: 'Mar',
  Wednesday: 'Mié',
  Thursday: 'Jue',
  Friday: 'Vie',
  Saturday: 'Sáb',
  Sunday: 'Dom',
};

// Chip colours per card status, so the row of days doubles as a status overview.
const CHIP_TONE: Record<ProposalStatus, { bg: string; fg: string; border: string }> = {
  pending: { bg: '#f0fbfa', fg: '#0f766e', border: '#99e6e0' },
  applying: { bg: '#f0fbfa', fg: '#0f766e', border: '#99e6e0' },
  applied: { bg: '#ecfdf5', fg: '#059669', border: '#a7f3d0' },
  discarded: { bg: '#f3f4f6', fg: '#6b7280', border: '#e5e7eb' },
  error: { bg: '#fef2f2', fg: '#dc2626', border: '#fecaca' },
};

const ProposalDayGroupCard: React.FC<ProposalDayGroupCardProps> = ({
  proposals,
  onResolve,
  onResolveMany,
}) => {
  const [expanded, setExpanded] = useState(false);

  const first = proposals[0];
  const pendingIds = proposals
    .filter((p) => p.id && p.status === 'pending')
    .map((p) => p.id as string);
  const appliedCount = proposals.filter((p) => p.status === 'applied').length;
  const discardedCount = proposals.filter((p) => p.status === 'discarded').length;

  const summary = (() => {
    const parts: string[] = [];
    if (appliedCount) parts.push(`${appliedCount} aplicadas`);
    if (discardedCount) parts.push(`${discardedCount} descartadas`);
    if (pendingIds.length) parts.push(`${pendingIds.length} sin revisar`);
    return parts.join(' · ');
  })();

  // Ungrouped: fall back to the regular one-card-per-day rendering, with a way
  // to fold back. Each card keeps its own per-day Confirm/Discard buttons.
  if (expanded) {
    return (
      <div style={{ marginTop: 8 }}>
        <button
          onClick={() => setExpanded(false)}
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: '4px 10px',
            background: '#fff',
            color: '#0f766e',
            fontWeight: 600,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          ⊟ Agrupar {proposals.length} días
        </button>
        {proposals.map((p, i) => (
          <ProposalCard
            key={p.id ?? i}
            proposal={p}
            onConfirm={() => p.id && onResolve(p.id, 'accept')}
            onDiscard={() => p.id && onResolve(p.id, 'reject')}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderLeft: '3px solid #38b2ac',
        borderRadius: 10,
        background: '#fff',
        padding: 12,
        marginTop: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: '#1f2937', flex: 1 }}>
          {proposalTitle(first.action)}
        </span>
      </div>

      {first.rationale && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, fontStyle: 'italic' }}>
          {first.rationale}
        </div>
      )}

      {/* Shared payload, with the per-day field folded into the chips below. */}
      <ProposalPayloadView payload={first.payload || {}} omit={['day_of_week']} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
        {proposals.map((p, i) => {
          const day = String(p.payload?.day_of_week ?? '');
          const tone = CHIP_TONE[p.status];
          return (
            <span
              key={p.id ?? i}
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: 999,
                background: tone.bg,
                color: tone.fg,
                border: `1px solid ${tone.border}`,
              }}
            >
              {DAY_LABELS[day] || day}
            </span>
          );
        })}
      </div>

      {summary && (
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>{summary}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {pendingIds.length > 0 && (
          <>
            <button
              onClick={() => onResolveMany(pendingIds, 'accept')}
              style={{
                border: 'none',
                borderRadius: 8,
                padding: '6px 14px',
                background: '#38b2ac',
                color: '#fff',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Confirmar {pendingIds.length} días
            </button>
            <button
              onClick={() => onResolveMany(pendingIds, 'reject')}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '6px 14px',
                background: '#fff',
                color: '#6b7280',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Descartar
            </button>
          </>
        )}
        <button
          onClick={() => setExpanded(true)}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#0f766e',
            fontWeight: 600,
            fontSize: 12,
            cursor: 'pointer',
            padding: '6px 4px',
            marginLeft: pendingIds.length > 0 ? 'auto' : 0,
          }}
        >
          Ver por día
        </button>
      </div>
    </div>
  );
};

export default ProposalDayGroupCard;
